import { InstallAgentPanelView } from '../view/InstallAgentPanelView';
import { templateInstallAgentPanel } from '../view/template';
import { InstallAgentPanelRepository } from '../repository/InstallAgentPanelRepository';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../../framework/BaseWebComponentController';
import { InstallAgentPanelWebComponent } from '..';
import { InstallAgentPanelEvents, OnInstallAgentPanelClose } from '../events/InstallAgentPanelEvents';
import { combineLatest, Subscription } from 'rxjs';
import { CompanyData, DeployerDataForInstall, GetPackageByIdResponse, InstallAgentViewData } from '../types';
import { DropdownEvents } from '../../../../ui/gzDropdown/types';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { ButtonEvents } from '../../../../ui/gzButton/types';
import { EndpointModules, PackageData } from '../../../../packages/packagesGrid/types';
import { PackageActionContext } from '../../../../packages/common/types/PackageActionContext';
import { MonthlyProductType } from '../../../../license/types/MonthlyProductType';
import {
  OnOperationModeSelect,
  PackageGeneralEvent,
} from '../../../../packages/common/packageGeneralForm/types/PackageGeneralEvent';
import {
  OnCustomScanModeOptionChanged,
  OnScanModeRadioSelectionChanged,
  PackageScanModesEvent,
} from '../../../../packages/common/packageScanModesForm/types/PackageScanModesEvent';
import { ScanModes } from '../../../../packages/common/packageScanModesForm/types/ScanModes';
import { DataProviderType, Packages } from '../../../../utils/request';
import { Dependencies } from '../../../types/install-agent-dependencies';
import { InputEvents } from '../../../../ui/gzInput/types';
import { GetCompanyPackagesResponse } from '../../../../utils/response';
import { LanguageData } from '../../../../packages/types/language-data';
import { InstallAgentTaskPayload } from '../../../../utils/request/tasks';
import _ from 'lodash';
import { DeepPartial } from 'typeorm';
import moment from 'moment/moment';
import { translateFn } from '../../../../utils/translator.helper';
import { TaskStatus, TaskType } from '../../../../tasks/networkTasks/types';
import { SchedulerDataResponse } from '../../../reconfigureAgent/common/reconfigureAgentShedulerForm/types';
import {
  CONNECT_TO_DEPLOYER_OPTIONS,
  DeployerDataResponse,
  DeployType,
} from '../../../../packages/common/packageDeployerForm/types';
import { DeployerGridEvents } from '../../../../packages/common/deployerGrid/types';
import {
  OnRemoveCompetitorsSelect,
  PackageModulesEvent,
} from '../../../../packages/common/packageModulesForm/types/PackageModulesEvent';
import { showToast } from '@nirvana/shared/common';
import { ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { Feature } from '@nirvana/shared/constants';

export class InstallAgentPanelController extends BaseWebComponentController {
  public component: InstallAgentPanelWebComponent;
  public dataRepository: InstallAgentPanelRepository;
  public view: InstallAgentPanelView;
  public viewConstructor: typeof InstallAgentPanelView;
  public dataRepositoryConstructor: typeof InstallAgentPanelRepository;
  public dependencies: Dependencies;
  private validSubscription: Subscription | null;
  private isRelayInstall: boolean;
  private _company: CompanyData;
  private _selectedPackage: PackageData;
  private featuresToCompanyMapping = new Map<string, Feature[]>();

  constructor(component: InstallAgentPanelWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: InstallAgentPanelView,
      viewTemplate: templateInstallAgentPanel,
      dataRepositoryConstructor: InstallAgentPanelRepository,
    });
  }

  get company(): CompanyData {
    return this._company;
  }

  set company(value: CompanyData) {
    this._company = value;
  }

  get selectedPackage(): PackageData {
    return this._selectedPackage;
  }

  set selectedPackage(value: PackageData) {
    this._selectedPackage = value;
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$wrapper,
        event: InstallAgentPanelEvents.OnViewInit,
        callback: this.showInstallAgentPanel,
      },
      {
        element: this.view.dom.$packageFormField,
        event: DropdownEvents.OPTION_SELECTED,
        callback: this.onPackageSelected,
      },
      {
        element: this.view.dom.$customizePackageButton,
        event: ButtonEvents.CLICK,
        callback: this.showPackage,
      },
      {
        element: this.view.dom.$wrapper,
        event: PackageGeneralEvent.OnOperationModeSelect,
        callback: this.onOperationModeSelect,
      },
      {
        element: this.view.dom.$packageScanModesForm,
        event: PackageScanModesEvent.OnCustomScanModeOptionChanged,
        callback: this.onCustomScanModeChange,
      },
      {
        element: this.view.dom.$packageScanModesForm,
        event: PackageScanModesEvent.OnScanModeRadioSelectionChanged,
        callback: this.onScanModeRadioSelectionChange,
      },
      {
        element: this.view.dom.$wrapper,
        event: PackageScanModesEvent.OnInit,
        callback: this.onCustomScanModeFinishInit,
      },
      {
        element: this.view.dom.$saveAsPackageCheckbox,
        event: InputEvents.CHANGE,
        callback: this.onSaveAsPackageChange,
      },
      {
        element: this.view.dom.$cancelButton,
        event: ButtonEvents.CLICK,
        callback: this.emitCloseInstallAgentWindowEvent,
      },
      {
        element: this.view.dom.$saveButton,
        event: ButtonEvents.CLICK,
        callback: this.saveInstallAgentTask,
      },
      {
        element: this.view.dom.$wrapper,
        event: PackageModulesEvent.OnRemoveCompetitorsSelect,
        callback: this.handleRemoveCompetitorsCheckbox,
      },
      {
        element: this.view.dom.$automaticallyRebootCheckbox,
        event: InputEvents.CHANGE,
        callback: this.handleAutomaticallyRebootCheckboxChange,
      },
      {
        element: this.view.dom.$installAgentDeployerForm,
        event: DeployerGridEvents.CONNECT_TO_SELECTED_EVENT,
        callback: this.handleRelayModuleAvailability,
      },
    ];
  }

  private handleRelayModuleAvailability(event: WebComponentEvent): void {
    this.view.dom.$packageFormField.validate();
    this.isRelayInstall = event.detail.selected;

    if (!this.view.dom.$packageModulesForm.started) {
      return;
    }

    if (!this.isRelayInstall) {
      this.view.dom.$packageModulesForm.disableRelayCheckboxModule = this.isRelayInstall;
      this.view.dom.$packageModulesForm.toggleOffRelayModule = !this.selectedPackage.settings.modules?.updateServer;
      return;
    }

    this.view.dom.$packageModulesForm.disableRelayCheckboxModule = this.isRelayInstall;
    this.view.dom.$packageModulesForm.toggleOffRelayModule = this.isRelayInstall;
  }

  private handleAutomaticallyRebootCheckboxChange(): void {
    this.view.handleAutomaticallyRebootInfoVisibility(!this.view.dom.$automaticallyRebootCheckbox.value);
  }

  private showInstallAgentPanel(): void {
    this.dependencies.isGZ() ? this.setOnPremValidationSubscription() : this.setDefaultValidationSubscription();
  }

  private setDefaultValidationSubscription(): void {
    this.validSubscription?.unsubscribe();
    this.validSubscription = combineLatest([
      this.view.dom.$packageFormField.valid$,
      this.view.dom.$installAgentDeployerForm.valid$,
      this.view.dom.$optionsSchedulerForm.valid$,
      this.view.dom.$installCredentialManagerGrid.valid$,
    ]).subscribe({
      next: ([
        packageFormField,
        installAgentDeployerValid,
        installAgentSchedulerValid,
        installCredentialManagerValidation,
      ]) => {
        this.view.dom.$saveButton.disabled = !(
          packageFormField &&
          installAgentDeployerValid &&
          installAgentSchedulerValid &&
          installCredentialManagerValidation
        );
      },
    });
  }

  private setOnPremValidationSubscription(): void {
    this.validSubscription?.unsubscribe();
    this.validSubscription = combineLatest([
      this.view.dom.$packageFormField.valid$,
      this.view.dom.$installAgentDeployerForm.valid$,
      this.view.dom.$optionsSchedulerForm.valid$,
      this.view.dom.$installCredentialManagerGrid.valid$,
      this.view.dom.$installAgentAdditionalTargetsFormField.valid$,
    ]).subscribe({
      next: ([
        packageFormField,
        installAgentDeployerValid,
        installAgentSchedulerValid,
        installCredentialManagerValidation,
        installAgentAdditionalTargetsValid,
      ]) => {
        this.view.dom.$saveButton.disabled = !(
          packageFormField &&
          installAgentDeployerValid &&
          installAgentSchedulerValid &&
          installCredentialManagerValidation &&
          installAgentAdditionalTargetsValid
        );
      },
    });
  }

  private setPackageValidationSubscription(): void {
    this.validSubscription?.unsubscribe();
    this.validSubscription = combineLatest([
      this.view.dom.$packageFormField.valid$,
      this.view.dom.$installAgentDeployerForm.valid$,
      this.view.dom.$optionsSchedulerForm.valid$,
      this.view.dom.$installCredentialManagerGrid.valid$,
      this.view.dom.$packageModulesForm.valid$,
      this.view.dom.$packageSecurityServerAssignmentForm.valid$,
      this.view.dom.$packageSettingsForm.valid$,
      this.view.dom.$packageUseProxyForm.valid$,
      this.view.dom.$saveAsPackageFormField.valid$,
    ]).subscribe({
      next: ([
        packageFormField,
        installAgentDeployerValid,
        installAgentSchedulerValid,
        installCredentialManagerValidation,
        packageModulesValid,
        packageSecurityServerAssignmentValid,
        packageSettingsFormValid,
        packageUseProxyFormValid,
        saveAsPackageValid,
      ]) => {
        this.view.dom.$saveButton.disabled = !(
          packageFormField &&
          installAgentDeployerValid &&
          installAgentSchedulerValid &&
          installCredentialManagerValidation &&
          packageModulesValid &&
          packageSecurityServerAssignmentValid &&
          packageSettingsFormValid &&
          packageUseProxyFormValid &&
          saveAsPackageValid
        );
      },
    });
  }

  private setPackageValidationSubscriptionOnPrem(): void {
    this.validSubscription?.unsubscribe();
    this.validSubscription = combineLatest([
      this.view.dom.$packageFormField.valid$,
      this.view.dom.$installAgentDeployerForm.valid$,
      this.view.dom.$optionsSchedulerForm.valid$,
      this.view.dom.$installCredentialManagerGrid.valid$,
      this.view.dom.$packageModulesForm.valid$,
      this.view.dom.$packageSecurityServerAssignmentForm.valid$,
      this.view.dom.$packageSettingsForm.valid$,
      this.view.dom.$packageUseProxyForm.valid$,
      this.view.dom.$saveAsPackageFormField.valid$,
      this.view.dom.$installAgentAdditionalTargetsFormField.valid$,
    ]).subscribe({
      next: ([
        packageFormField,
        installAgentDeployerValid,
        installAgentSchedulerValid,
        installCredentialManagerValidation,
        packageModulesValid,
        packageSecurityServerAssignmentValid,
        packageSettingsFormValid,
        packageUseProxyFormValid,
        saveAsPackageValid,
        installAgentAdditionalTargetsValid,
      ]) => {
        this.view.dom.$saveButton.disabled = !(
          packageFormField &&
          installAgentDeployerValid &&
          installAgentSchedulerValid &&
          installCredentialManagerValidation &&
          packageModulesValid &&
          packageSecurityServerAssignmentValid &&
          packageSettingsFormValid &&
          packageUseProxyFormValid &&
          saveAsPackageValid &&
          installAgentAdditionalTargetsValid
        );
      },
    });
  }

  private onOperationModeSelect(event: OnOperationModeSelect): void {
    if (event.detail.productType === this.selectedPackage.settings.productType) {
      this.view.dom.$packageModulesForm.packageData = {
        productType: this.selectedPackage.settings.productType,
        modules: this.selectedPackage.settings.modules,
        removeCompetitors: this.selectedPackage.settings.removeCompetitors,
      };
    }

    this.view.dom.$packageScanModesForm.toggleVisibilityBasedOnProductType(event.detail.productType);
    this.toggleVisibilityRemoveCompetitorInfo();
    this.view.dom.$packageMiscellaneousForm.showSubmitQuarantineSectionByOperationMode(event.detail.productType);
  }

  private async onCustomScanModeChange(ev: OnCustomScanModeOptionChanged): Promise<void> {
    this.view.dom.$packageScanModesForm.useDefaultValues = false;
    await this.view.dom.$packageSecurityServerAssignmentForm.showSecurityServerAssignmentSection(
      ev.detail.hasCentralScan,
      this._company.id
    );
  }

  private async onScanModeRadioSelectionChange(ev: OnScanModeRadioSelectionChanged): Promise<void> {
    const scanType: ScanModes = ev.detail.selectedValue;
    const hasCustomModeOptions = this.view.dom.$packageScanModesForm.hasCustomModeOptions(scanType);

    await this.view.dom.$packageSecurityServerAssignmentForm.showSecurityServerAssignmentSection(
      hasCustomModeOptions,
      this._company.id
    );
  }

  private async onCustomScanModeFinishInit(ev: WebComponentEvent): Promise<void> {
    const isVisible = this.view.dom.$packageScanModesForm.hasCentralScan(ev.detail);
    await this.view.dom.$packageSecurityServerAssignmentForm.showSecurityServerAssignmentSection(
      isVisible,
      this._company.id
    );
  }

  private onSaveAsPackageChange(ev: WebComponentEvent): void {
    this.view.dom.$saveAsPackageFormField.disabled = !ev.detail.checked;
  }

  private handleRemoveCompetitorsCheckbox(event: OnRemoveCompetitorsSelect): void {
    this.component.toggleVisibilityRemoveCompetitorInfo(event.detail.isRemoveCompetitorsChecked);
  }

  private emitCloseInstallAgentWindowEvent(): void {
    this.component.dispatchEvent(new OnInstallAgentPanelClose());
  }

  private async saveInstallAgentTask(): Promise<void> {
    this.view.dom.$saveButton.showSpinner = true;
    this.view.dom.$saveButton.disabled = true;

    let responseState = false;

    try {
      await this.createInstallAgentTask();
      responseState = true;
    } catch (e) {} // error logging of the Exception is handled in the ExtJs code for now

    this.view.dom.$saveButton.showSpinner = false;
    this.view.dom.$saveButton.disabled = false;

    this.emitCloseInstallAgentWindowEvent();
  }

  private getTaskTypeByDeployType(deployType: number): TaskType {
    if (deployType && deployType === CONNECT_TO_DEPLOYER_OPTIONS[DeployType.EndpointSecurityRelay].value) {
      return TaskType.TASK_TYPE_INSTALL_ENDPOINT_SUPA_PARENT;
    }
    return TaskType.TASK_TYPE_INSTALL_ENDPOINT_PARENT;
  }

  private createInstallAgentTask(): Promise<void> {
    const targets = this.dependencies.targets();
    const schedulerData: SchedulerDataResponse = this.view.dom.$optionsSchedulerForm.value;
    const deployerData: DeployerDataResponse = this.view.dom.$installAgentDeployerForm.value;
    const credentialsManagerData = this.view.dom.$installCredentialManagerGrid.value;
    const taskType: TaskType = this.getTaskTypeByDeployType(deployerData.settings.deployType);
    const deployerDataForInstall: DeployerDataForInstall = this.parseDeployerDataForInstallTask(deployerData);

    let installAgentPayload: DeepPartial<InstallAgentTaskPayload> = _.merge(schedulerData, deployerDataForInstall, {
      settings: {
        specifics: _.merge(
          {
            settings: {
              autoReboot: this.view.dom.$automaticallyRebootCheckbox.value,
            },
            upgradeTask: false,
          },
          credentialsManagerData
        ),
        packageId: this.view.dom.$packageFormField.Input.value as string,
        status: TaskStatus.STATUS_PROCESSING,
        recordSource: DataProviderType.ENDPOINTS,
        targets: targets,
        targetsCount: targets.length,
        progress: 0,
        customPackage: this.view.customPackage,
        taskName: this.getTaskName(schedulerData.settings.specifics.scheduler.scheduledDeploy),
        type: taskType,
      },
    }) as InstallAgentTaskPayload;

    if (this.dependencies.isGZ()) {
      installAgentPayload = _.merge(this.view.dom.$installAgentAdditionalTargetsFormField.value, installAgentPayload);
    }

    if (this.view.customPackage) {
      const settingsFormData = this.view.dom.$packageSettingsForm.value;
      if (settingsFormData.settings && settingsFormData.hasOwnProperty('setUninstallPassword')) {
        settingsFormData.settings['setUninstallPassword'] = settingsFormData.setUninstallPassword;
        delete settingsFormData.setUninstallPassword;
      }

      installAgentPayload = _.merge(installAgentPayload, this.component.value, {
        settings: {
          specifics: _.merge(
            this.view.dom.$packageModulesForm.value,
            this.view.dom.$packageScanModesForm.value,
            this.view.dom.$packageSecurityServerAssignmentForm.value,
            this.view.dom.$packageMiscellaneousForm.value,
            settingsFormData,
            this.view.dom.$packageUseProxyForm.value
          ),
        },
      });
    }

    return this.dependencies.makeRequest(installAgentPayload);
  }

  private getTaskName(scheduledDeploy: boolean = false): string {
    const currentDate = moment(new Date()).format('YYYY-MM-DD');
    if (!scheduledDeploy) {
      return translateFn(`Install agent {currentDate}`, {
        currentDate: currentDate,
      });
    }

    return translateFn(`Scheduled Agent Installation {currentDate}`, {
      currentDate: currentDate,
    });
  }

  async getDataForView(): Promise<InstallAgentViewData | void> {
    this.company = this.dependencies.selectedCompany();
    let hasNoCompanySelected = false;
    if (!this.company) {
      this.company = this.dependencies.getCurrentUserCompany();
      hasNoCompanySelected = true;
    }

    let companyPackages: GetCompanyPackagesResponse;
    try {
      companyPackages = await this.dataRepository.getCompanyPackages(this.company.id);
    } catch (e) {
      this.emitCloseInstallAgentWindowEvent();
      showToast(translateFn('Something went wrong...'), ToastSeverity.ERROR);
      return;
    }
    const languages: LanguageData[] = this.dataRepository.getLanguages();

    return {
      company: this.company,
      companyPackages: companyPackages.data,
      languages: languages,
      hasNoCompanySelected: hasNoCompanySelected,
    };
  }

  private async onPackageSelected(ev: WebComponentEvent): Promise<void> {
    if (ev.detail.oldValue && ev.detail.oldValue.value === ev.detail.newValue.value) {
      return;
    }

    this.dependencies.isGZ() ? this.setOnPremValidationSubscription() : this.setDefaultValidationSubscription();
    this.view.customPackage = false;

    toggleVisibility(this.view.dom.$packageWrapper, false);

    const featuresToCheck = await this.getSelectedCompanyFeatures(this.company.id);

    if (featuresToCheck.length === 0 || !featuresToCheck[Feature.PRODUCT_FEATURE_FRAT_VISIBLE]) {
      this.view.dom.$customizePackageButton.disabled = !ev.detail.newValue;
    }
  }

  private async getSelectedCompanyFeatures(companyId: string): Promise<Feature[]> {
    if (!this.featuresToCompanyMapping.has(companyId)) {
      const features = await this.dataRepository.getFeaturesByCompany({
        companyId: companyId,
      });
      this.featuresToCompanyMapping.set(companyId, features);
    }

    return this.featuresToCompanyMapping.get(companyId) ?? [];
  }

  public toggleVisibilityRemoveCompetitorInfo(): void {
    toggleVisibility(
      this.view.dom.$removeCompetitorInfo,
      this.view.dom.$packageModulesForm.operationMode !== MonthlyProductType.BitdefenderEdr
    );
  }

  private async showPackage(): Promise<void> {
    this.view.dom.$customizePackageButton.setLoadingState(true);
    toggleVisibility(this.view.dom.$customizePackageInfo, false);
    this.view.customPackage = true;
    const selectedPackageId: string = this.view.dom.$packageFormField.Input.value as string;
    let packageResponse: GetPackageByIdResponse;

    try {
      packageResponse = await this.dataRepository.getPackageById({
        id: selectedPackageId,
        type: Packages.Types.Endpoints,
      });
    } catch (e) {
      toggleVisibility(this.view.dom.$customizePackageInfo, true);
      this.view.dom.$customizePackageButton.setLoadingState(false);
      return;
    }

    this.selectedPackage = packageResponse.package;

    const localFeatures = await this.getSelectedCompanyFeatures(this.company.id);

    this.view.formGroup.value = {
      settings: {
        specifics: {
          settings: {
            language: this.selectedPackage.language,
          },
        },
      },
    };

    this.view.dom.$saveAsPackageCheckbox.value = false;
    this.view.dom.$saveAsPackageFormField.value = '';

    this.view.dom.$packageModulesForm.formContext = PackageActionContext.Edit;
    this.view.dom.$packageModulesForm.useDefaultValues = true;
    this.view.dom.$packageModulesForm.setAvailableFeatures(localFeatures);
    this.view.dom.$packageModulesForm.injectDependencies(this.dependencies);

    this.view.dom.$packageScanModesForm.formContext = PackageActionContext.Edit;
    this.view.dom.$packageScanModesForm.setContextFrat(false);
    this.view.dom.$packageScanModesForm.setFeatures(localFeatures);
    this.view.dom.$packageScanModesForm.injectDependencies(this.dependencies);

    await this.view.dom.$packageSecurityServerAssignmentForm.setPackageData(this.selectedPackage);
    this.view.dom.$packageSecurityServerAssignmentForm.injectDependencies(this.dependencies);
    this.view.dom.$packageSecurityServerAssignmentForm.companyId = this._company.id;

    this.view.dom.$packageMiscellaneousForm.injectDependencies(this.dependencies);

    this.view.dom.$packageSettingsForm.setPackageType(this.selectedPackage.type);
    this.view.dom.$packageSettingsForm.setPackageData(this.selectedPackage);
    this.view.dom.$packageSettingsForm.injectDependencies(this.dependencies);

    this.view.dom.$packageUseProxyForm.injectDependencies(this.dependencies);

    const startWebCompPromises: Promise<void>[] = [
      this.view.dom.$packageModulesForm.start(),
      this.view.dom.$packageScanModesForm.start(),
      this.view.dom.$packageSecurityServerAssignmentForm.start(),
      this.view.dom.$packageMiscellaneousForm.start(),
      this.view.dom.$packageSettingsForm.start(),
      this.view.dom.$packageUseProxyForm.start(),
    ];

    Promise.all(startWebCompPromises)
      .then(async () => {
        this.view.dom.$packageModulesForm.controller.view.formGroup.setDisabled(false);
        this.view.dom.$packageModulesForm.packageData = {
          productType: this.selectedPackage.settings.productType,
          modules: this.selectedPackage.settings.modules as EndpointModules,
          removeCompetitors: this.selectedPackage.settings.removeCompetitors,
        };
        this.view.dom.$packageModulesForm.controller.setOperationMode();

        if (this.selectedPackage.settings.scanMode?.type === ScanModes.Automatic) {
          this.view.dom.$packageScanModesForm.useDefaultValues = true;
        }
        this.view.dom.$packageScanModesForm.setPackageData(this.selectedPackage, localFeatures);
        this.view.dom.$packageMiscellaneousForm.setPackageData(this.selectedPackage);
        this.view.dom.$packageUseProxyForm.setFormValuesFromDatabase(this.selectedPackage);
        this.view.dom.$packageSettingsForm.enableSettingsSection();
        await this.view.dom.$packageSecurityServerAssignmentForm.render();

        if (this.isRelayInstall) {
          this.view.dom.$packageModulesForm.toggleOffRelayModule = this.isRelayInstall;
          this.view.dom.$packageModulesForm.disableRelayCheckboxModule = this.isRelayInstall;
        }

        this.dependencies.isGZ()
          ? this.setPackageValidationSubscriptionOnPrem()
          : this.setPackageValidationSubscription();
        this.view.dom.$packageFormField.validate();
      })
      .catch(console.error)
      .finally(() => {
        this.view.dom.$customizePackageButton.showSpinner = false;
        toggleVisibility(this.view.dom.$packageWrapper, true);
      });
  }

  private parseDeployerDataForInstallTask(deployerData: DeployerDataResponse): DeployerDataForInstall {
    const response: DeployerDataForInstall = {
      settings: {
        specifics: {
          settings: {
            deployType: deployerData.settings.deployType,
            useCustomECS: deployerData.settings.useCustomECS,
          },
          ecsHostname: deployerData.settings.ecsHostname,
        },
      },
    };

    if (deployerData.settings.supaId) {
      response.settings.specifics.supaId = deployerData.settings.supaId;
    }

    return response;
  }
}
